import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type {
  ApiError,
  BridgeHeartbeatRequest,
  BridgePairRequest,
  BridgePairResponse,
  BusinessDay,
  CatalogSnapshot,
  LoginRequest,
  LoginResponse,
  MeResponse,
  Ticket,
  TicketPack,
  Uuid,
} from '@positec/types';

export class PositecApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'PositecApiError';
    this.status = status;
    this.errors = errors;
  }
}

export type PositecClientOptions = {
  baseURL: string;
  /** Cómo obtener el bearer token actual (puede cambiar dinámicamente). */
  getToken?: () => string | null;
  /** Slug del tenant para forzar (header X-Tenant-Slug). */
  tenantSlug?: () => string | null;
  /** Callback al recibir 401 (sesión inválida). */
  onUnauthenticated?: () => void;
};

/** Modo de conexión activo en el TPV. */
export type ConnectionMode = 'bridge' | 'cloud' | 'offline';

export type DiscoveryResult = {
  /** URL base que se usará para todas las llamadas (Bridge local o cloud). */
  baseURL: string;
  mode: ConnectionMode;
  /** Información del bridge si se descubrió. */
  bridge?: {
    bridge_id: string;
    tenant_id: string;
    locale_id: string;
    bridge_name: string;
    version: string;
  };
};

/**
 * Intenta descubrir un Bridge local en `localhost:31415`. Si responde
 * `/healthz` antes del timeout, se devuelve la URL del Bridge. Si no,
 * se devuelve la URL del cloud configurada como fallback.
 *
 * Pensado para llamarse al arrancar el TPV.
 */
export async function discoverConnection(opts: {
  bridgeURL?: string;
  cloudURL: string;
  timeoutMs?: number;
}): Promise<DiscoveryResult> {
  const bridgeURL = opts.bridgeURL ?? 'http://127.0.0.1:31415';
  const timeout = opts.timeoutMs ?? 800;

  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeout);
    const r = await fetch(`${bridgeURL}/healthz`, {
      signal: ctrl.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(t);
    if (r.ok) {
      const info = (await r.json()) as DiscoveryResult['bridge'] & { status: string };
      if (info?.status === 'ok') {
        return {
          baseURL: bridgeURL,
          mode: 'bridge',
          bridge: {
            bridge_id: info.bridge_id,
            tenant_id: info.tenant_id,
            locale_id: info.locale_id,
            bridge_name: info.bridge_name,
            version: info.version,
          },
        };
      }
    }
  } catch {
    // sin Bridge local — caemos a cloud
  }

  return { baseURL: opts.cloudURL, mode: 'cloud' };
}

export function createPositecClient(opts: PositecClientOptions): PositecClient {
  const http: AxiosInstance = axios.create({
    baseURL: opts.baseURL.replace(/\/$/, ''),
    headers: { Accept: 'application/json' },
    timeout: 15000,
    // Forzar sin cookies: todos los frontends usan Bearer token.
    // Si el navegador tiene XSRF-TOKEN/positec_session heredadas, no las
    // mandamos para que el backend no active flujo SPA stateful.
    withCredentials: false,
  });

  http.interceptors.request.use((config) => {
    const token = opts.getToken?.();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const slug = opts.tenantSlug?.();
    if (slug) {
      config.headers['X-Tenant-Slug'] = slug;
    }
    return config;
  });

  http.interceptors.response.use(
    (r) => r,
    (error) => {
      if (error.response) {
        const data = (error.response.data ?? {}) as ApiError;
        const positecErr = new PositecApiError(
          data.message ?? error.message,
          error.response.status,
          data.errors,
        );
        if (error.response.status === 401) {
          opts.onUnauthenticated?.();
        }
        return Promise.reject(positecErr);
      }
      return Promise.reject(new PositecApiError(error.message, 0));
    },
  );

  return new PositecClient(http);
}

export class PositecClient {
  constructor(private readonly http: AxiosInstance) {}

  // --- Auth ---------------------------------------------------------------
  async login(body: LoginRequest): Promise<LoginResponse> {
    const { data } = await this.http.post<LoginResponse>('/api/v1/auth/login', body);
    return data;
  }

  async me(): Promise<MeResponse> {
    const { data } = await this.http.get<MeResponse>('/api/v1/auth/me');
    return data;
  }

  async logout(): Promise<void> {
    await this.http.post('/api/v1/auth/logout');
  }

  async logoutAll(): Promise<void> {
    await this.http.post('/api/v1/auth/logout-all');
  }

  // --- Public -------------------------------------------------------------
  async ticketPacks(): Promise<TicketPack[]> {
    const { data } = await this.http.get<TicketPack[]>('/api/v1/ticket-packs');
    return data;
  }

  async registerTenant(body: RegisterTenantRequest): Promise<RegisterTenantResponse> {
    const { data } = await this.http.post<RegisterTenantResponse>('/api/v1/auth/register-tenant', body);
    return data;
  }

  // --- Bridge -------------------------------------------------------------
  async bridgePair(body: BridgePairRequest): Promise<BridgePairResponse> {
    const { data } = await this.http.post<BridgePairResponse>('/api/v1/bridges/pair', body);
    return data;
  }

  async bridgeHeartbeat(body: BridgeHeartbeatRequest): Promise<{ received_at: string; bridge_id: string }> {
    const { data } = await this.http.post<{ received_at: string; bridge_id: string }>(
      '/api/v1/bridges/heartbeat',
      body,
    );
    return data;
  }

  // --- Catálogo / POS -----------------------------------------------------
  async catalogSnapshot(): Promise<CatalogSnapshot> {
    const { data } = await this.http.get<CatalogSnapshot>('/api/v1/catalog/snapshot');
    return data;
  }

  async currentBusinessDay(localeId: Uuid): Promise<BusinessDay | null> {
    const { data } = await this.http.get<BusinessDay | null>(
      '/api/v1/pos/business-day/current',
      { params: { locale_id: localeId } },
    );
    return data;
  }

  async openBusinessDay(localeId: Uuid, openingCash = 0): Promise<BusinessDay> {
    const { data } = await this.http.post<BusinessDay>(
      '/api/v1/pos/business-day/open',
      { locale_id: localeId, opening_cash: openingCash },
    );
    return data;
  }

  async ticketByLocation(locationId: Uuid): Promise<Ticket | null> {
    const { data } = await this.http.get<Ticket | null>(
      `/api/v1/pos/sales-locations/${locationId}/ticket`,
    );
    return data;
  }

  async createTicket(payload: {
    sales_center_id: Uuid;
    sales_location_id?: Uuid;
    diners?: number;
  }): Promise<Ticket> {
    const { data } = await this.http.post<Ticket>('/api/v1/pos/tickets', payload);
    return data;
  }

  async getTicket(ticketId: Uuid): Promise<Ticket> {
    const { data } = await this.http.get<Ticket>(`/api/v1/pos/tickets/${ticketId}`);
    return data;
  }
  async addTicketLine(ticketId: Uuid, payload: {
    product_id: Uuid;
    format_id?: Uuid;
    quantity?: number;
    notes?: string;
  }): Promise<Ticket> {
    const { data } = await this.http.post<Ticket>(
      `/api/v1/pos/tickets/${ticketId}/lines`,
      payload,
    );
    return data;
  }

  async deleteTicketLine(ticketId: Uuid, lineId: Uuid): Promise<Ticket> {
    const { data } = await this.http.delete<Ticket>(
      `/api/v1/pos/tickets/${ticketId}/lines/${lineId}`,
    );
    return data;
  }

  async addTicketPayment(ticketId: Uuid, payload: {
    payment_method_id: Uuid;
    amount: number;
    tendered?: number;
    tip_amount?: number;
    reference?: string;
  }): Promise<Ticket> {
    const { data } = await this.http.post<Ticket>(
      `/api/v1/pos/tickets/${ticketId}/payments`,
      payload,
    );
    return data;
  }

  async cancelTicket(ticketId: Uuid): Promise<Ticket> {
    const { data } = await this.http.post<Ticket>(`/api/v1/pos/tickets/${ticketId}/cancel`);
    return data;
  }

  // --- KDS ----------------------------------------------------------------
  async kdsOrders(params?: { locale_id?: string; sales_center_id?: string }): Promise<KdsOrdersResponse> {
    const { data } = await this.http.get<KdsOrdersResponse>('/api/v1/kds/orders', { params });
    return data;
  }
  async kdsStart(lineId: string): Promise<KdsLineStatusResponse> {
    const { data } = await this.http.post<KdsLineStatusResponse>(`/api/v1/kds/lines/${lineId}/start`);
    return data;
  }
  async kdsReady(lineId: string): Promise<KdsLineStatusResponse> {
    const { data } = await this.http.post<KdsLineStatusResponse>(`/api/v1/kds/lines/${lineId}/ready`);
    return data;
  }
  async kdsServed(lineId: string): Promise<KdsLineStatusResponse> {
    const { data } = await this.http.post<KdsLineStatusResponse>(`/api/v1/kds/lines/${lineId}/served`);
    return data;
  }
  async kdsCancel(lineId: string, reason?: string): Promise<KdsLineStatusResponse> {
    const { data } = await this.http.post<KdsLineStatusResponse>(`/api/v1/kds/lines/${lineId}/cancel`, { reason });
    return data;
  }

  // --- Cash machines (cloud) ---------------------------------------------
  async cashMachines(): Promise<{ machines: CashMachine[] }> {
    const { data } = await this.http.get<{ machines: CashMachine[] }>('/api/v1/cash/machines');
    return data;
  }
  async cashMachine(id: string): Promise<CashMachine> {
    const { data } = await this.http.get<CashMachine>(`/api/v1/cash/machines/${id}`);
    return data;
  }
  async cashRecordMovement(
    id: string,
    payload: { kind: 'cash_in' | 'dispense' | 'manual_in' | 'manual_out' | 'arching' | 'adjust'; amount: number; reason?: string; ticket_payment_id?: string; denominations?: Record<string, number> },
  ): Promise<CashMovement> {
    const { data } = await this.http.post<CashMovement>(`/api/v1/cash/machines/${id}/movements`, payload);
    return data;
  }
  async cashListMovements(id: string): Promise<{ movements: CashMovement[]; balance: number }> {
    const { data } = await this.http.get<{ movements: CashMovement[]; balance: number }>(`/api/v1/cash/machines/${id}/movements`);
    return data;
  }

  // --- Cash machines (bridge local HTTP) ---------------------------------
  // Estos llamados suponen baseURL apuntando al Bridge local (modo bridge).
  async cashStatusLocal(): Promise<CashStatusLocal> {
    const { data } = await this.http.get<CashStatusLocal>('/cash/status');
    return data;
  }
  async cashAcceptLocal(amount: number): Promise<{ accepted: number; balance: number }> {
    const { data } = await this.http.post<{ accepted: number; balance: number }>('/cash/accept', { amount });
    return data;
  }
  async cashDispenseLocal(amount: number): Promise<{ requested: number; dispensed: number; remaining_balance: number }> {
    const { data } = await this.http.post<{ requested: number; dispensed: number; remaining_balance: number }>('/cash/dispense', { amount });
    return data;
  }

  // --- Fiscal (Fase 9) -----------------------------------------------------
  async getFiscalSettings(): Promise<FiscalSettings> {
    const { data } = await this.http.get<FiscalSettings>('/api/v1/fiscal/settings');
    return data;
  }
  async updateFiscalSettings(payload: Partial<FiscalSettings>): Promise<FiscalSettings> {
    const { data } = await this.http.patch<FiscalSettings>('/api/v1/fiscal/settings', payload);
    return data;
  }
  async listFiscalCertificates(): Promise<{ certificates: FiscalCertificate[] }> {
    const { data } = await this.http.get<{ certificates: FiscalCertificate[] }>('/api/v1/fiscal/certificates');
    return data;
  }
  async uploadFiscalCertificate(form: FormData): Promise<FiscalCertificate> {
    const { data } = await this.http.post<FiscalCertificate>('/api/v1/fiscal/certificates', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }
  async deleteFiscalCertificate(id: string): Promise<{ ok: boolean }> {
    const { data } = await this.http.delete<{ ok: boolean }>(`/api/v1/fiscal/certificates/${id}`);
    return data;
  }
  async listFiscalRecords(query: { status?: string; kind?: string; from?: string; to?: string; page?: number; per_page?: number } = {}): Promise<{ records: FiscalRecord[]; total: number; page: number; per_page: number }> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, String(v)); });
    const qs = params.toString();
    const { data } = await this.http.get<{ records: FiscalRecord[]; total: number; page: number; per_page: number }>(`/api/v1/fiscal/records${qs ? '?' + qs : ''}`);
    return data;
  }
  async retrySendFiscalRecord(id: string): Promise<FiscalRecord> {
    const { data } = await this.http.post<FiscalRecord>(`/api/v1/fiscal/records/${id}/retry-send`);
    return data;
  }


  // --- Reports (Fase 10) ----------------------------------------------------
  async reportDailySales(from: string, to: string): Promise<{ rows: any[]; totals: any }> {
    const { data } = await this.http.get(`/api/v1/reports/daily-sales?from=${from}&to=${to}`);
    return data;
  }
  async reportHourly(date: string): Promise<{ date: string; rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/hourly?date=${date}`);
    return data;
  }
  async reportByEmployee(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/by-employee?from=${from}&to=${to}`);
    return data;
  }
  async reportByCenter(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/by-center?from=${from}&to=${to}`);
    return data;
  }
  async reportByProduct(from: string, to: string, limit = 50): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/by-product?from=${from}&to=${to}&limit=${limit}`);
    return data;
  }
  async reportByFamily(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/by-family?from=${from}&to=${to}`);
    return data;
  }
  async reportPaymentMethods(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/payment-methods?from=${from}&to=${to}`);
    return data;
  }
  async reportTaxes(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/taxes?from=${from}&to=${to}`);
    return data;
  }
  async reportCancellations(from: string, to: string): Promise<{ rows: any[]; totals: any }> {
    const { data } = await this.http.get(`/api/v1/reports/cancellations?from=${from}&to=${to}`);
    return data;
  }
  async reportPromotions(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/promotions?from=${from}&to=${to}`);
    return data;
  }
  async reportTopUnsold(days = 30, limit = 50): Promise<{ rows: any[]; days: number }> {
    const { data } = await this.http.get(`/api/v1/reports/top-unsold?days=${days}&limit=${limit}`);
    return data;
  }
  async reportStockMovements(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/stock-movements?from=${from}&to=${to}`);
    return data;
  }
  async reportBySalesLocation(from: string, to: string): Promise<{ rows: any[] }> {
    const { data } = await this.http.get(`/api/v1/reports/by-sales-location?from=${from}&to=${to}`);
    return data;
  }
  async reportZ(businessDayId: string): Promise<any> {
    const { data } = await this.http.get(`/api/v1/reports/z-report/${businessDayId}`);
    return data;
  }
  async reportX(businessDayId: string): Promise<any> {
    const { data } = await this.http.get(`/api/v1/reports/x-report/${businessDayId}`);
    return data;
  }
  reportCsvUrl(reportType: string, params: Record<string, string>): string {
    const qs = new URLSearchParams({ ...params, format: 'csv' }).toString();
    const baseURL = (this.http.defaults.baseURL ?? "").replace(/\/$/, "");
    return `${baseURL}/api/v1/reports/${reportType}?${qs}`;
  }


  // --- Payment terminals (Fase 11, cloud) ----------------------------------
  async listPaymentTerminals(active_only = true): Promise<{ terminals: any[] }> {
    const { data } = await this.http.get(`/api/v1/payment-terminals?active_only=${active_only ? 1 : 0}`);
    return data;
  }
  async createPaymentTerminal(payload: any): Promise<any> {
    const { data } = await this.http.post('/api/v1/payment-terminals', payload);
    return data;
  }
  async updatePaymentTerminal(id: string, payload: any): Promise<any> {
    const { data } = await this.http.patch(`/api/v1/payment-terminals/${id}`, payload);
    return data;
  }
  async deletePaymentTerminal(id: string): Promise<{ ok: boolean }> {
    const { data } = await this.http.delete(`/api/v1/payment-terminals/${id}`);
    return data;
  }
  async startTerminalTransaction(terminalId: string, payload: { kind: 'sale' | 'refund' | 'void' | 'eod' | 'test'; amount?: number; ticket_id?: string; currency?: string }): Promise<any> {
    const { data } = await this.http.post(`/api/v1/payment-terminals/${terminalId}/transactions`, payload);
    return data;
  }
  async finalizeTerminalTransaction(terminalId: string, txId: string, payload: { approved: boolean; auth_code?: string; rrn?: string; card_brand?: string; card_last4?: string; response_code?: string; response_message?: string; raw_response?: any; amount?: number }): Promise<any> {
    const { data } = await this.http.post(`/api/v1/payment-terminals/${terminalId}/transactions/${txId}/finalize`, payload);
    return data;
  }
  async listTerminalTransactions(terminalId: string, query: { kind?: string; status?: string; page?: number; per_page?: number } = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => { if (v !== undefined && v !== null) params.set(k, String(v)); });
    const { data } = await this.http.get(`/api/v1/payment-terminals/${terminalId}/transactions${params.toString() ? '?' + params : ''}`);
    return data;
  }

  // --- Datafono (bridge local HTTP, Fase 11) -------------------------------
  async datafonoStatusLocal(): Promise<{ status: string; config_kind: string }> {
    const { data } = await this.http.get('/datafono/status');
    return data;
  }
  async datafonoSaleLocal(amount_cents: number, ticket_ref: string): Promise<DatafonoSaleResult> {
    const { data } = await this.http.post<DatafonoSaleResult>('/datafono/sale', { amount_cents, ticket_ref });
    return data;
  }
  async datafonoRefundLocal(amount_cents: number, original_rrn: string): Promise<DatafonoSaleResult> {
    const { data } = await this.http.post<DatafonoSaleResult>('/datafono/refund', { amount_cents, original_rrn });
    return data;
  }
  async datafonoVoidLocal(original_rrn: string): Promise<DatafonoSaleResult> {
    const { data } = await this.http.post<DatafonoSaleResult>('/datafono/void', { original_rrn });
    return data;
  }
  async datafonoEodLocal(): Promise<{ summary: string }> {
    const { data } = await this.http.post('/datafono/eod');
    return data;
  }


  // --- ACMS / Multilocal (Fase 13) -----------------------------------------
  async listOrganizations(): Promise<{ organizations: any[] }> {
    const { data } = await this.http.get('/api/v1/acms/organizations');
    return data;
  }
  async showOrganization(id: string): Promise<any> {
    const { data } = await this.http.get(`/api/v1/acms/organizations/${id}`);
    return data;
  }
  async createOrganization(payload: { name: string; slug?: string; cif_nif?: string }): Promise<any> {
    const { data } = await this.http.post('/api/v1/acms/organizations', payload);
    return data;
  }
  async updateOrganization(id: string, payload: any): Promise<any> {
    const { data } = await this.http.patch(`/api/v1/acms/organizations/${id}`, payload);
    return data;
  }
  async deleteOrganization(id: string): Promise<{ ok: boolean }> {
    const { data } = await this.http.delete(`/api/v1/acms/organizations/${id}`);
    return data;
  }
  async organizationDashboard(id: string): Promise<any> {
    const { data } = await this.http.get(`/api/v1/acms/organizations/${id}/dashboard`);
    return data;
  }
  async consolidatedReport(orgId: string | null, from: string, to: string): Promise<{ rows: any[]; totals: any }> {
    const url = orgId
      ? `/api/v1/acms/organizations/${orgId}/consolidated-report?from=${from}&to=${to}`
      : `/api/v1/acms/consolidated-report?from=${from}&to=${to}`;
    const { data } = await this.http.get(url);
    return data;
  }
  async listZones(organization_id?: string): Promise<{ zones: any[] }> {
    const qs = organization_id ? `?organization_id=${organization_id}` : '';
    const { data } = await this.http.get(`/api/v1/acms/zones${qs}`);
    return data;
  }
  async createZone(payload: { organization_id: string; name: string; code?: string }): Promise<any> {
    const { data } = await this.http.post('/api/v1/acms/zones', payload);
    return data;
  }
  async updateZone(id: string, payload: any): Promise<any> {
    const { data } = await this.http.patch(`/api/v1/acms/zones/${id}`, payload);
    return data;
  }
  async deleteZone(id: string): Promise<{ ok: boolean }> {
    const { data } = await this.http.delete(`/api/v1/acms/zones/${id}`);
    return data;
  }
  async assignLocaleToOrganization(localeId: string, payload: { organization_id?: string | null; zone_id?: string | null }): Promise<any> {
    const { data } = await this.http.patch(`/api/v1/acms/locales/${localeId}/assignment`, payload);
    return data;
  }


  // --- Raw access (escape hatch) ------------------------------------------
  raw<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.http.request<T>(config).then((r) => r.data);
  }
}

export type KdsLine = {
  id: string;
  product_name: string;
  quantity: number;
  notes: string | null;
  modifiers: Record<string, unknown> | null;
  preparation_status: 'pending' | 'started' | 'ready' | 'served' | 'cancelled';
  preparation_started_at: string | null;
  preparation_ready_at: string | null;
  sent_to_kitchen_at: string | null;
  waiting_seconds: number;
};

export type KdsOrder = {
  ticket_id: string;
  sales_center: { id: string; name: string } | null;
  sales_location: { id: string; name: string } | null;
  diners: number;
  opened_at: string | null;
  lines: KdsLine[];
};

export type KdsOrdersResponse = {
  orders: KdsOrder[];
  fetched_at: string;
};

export type KdsLineStatusResponse = {
  id: string;
  preparation_status: KdsLine['preparation_status'];
  preparation_started_at: string | null;
  preparation_ready_at: string | null;
  preparation_served_at: string | null;
};

export type CashMachine = {
  id: string;
  tenant_id: string;
  locale_id: string;
  point_of_sale_id: string | null;
  name: string;
  kind: 'cashlogy' | 'cashkeeper' | 'cashguard' | 'cashdro' | 'mock';
  model: string | null;
  is_online: boolean;
  is_active: boolean;
  balance: string;
  last_status_at: string | null;
};

export type CashMovement = {
  id: string;
  cash_machine_id: string;
  user_id: string | null;
  ticket_payment_id: string | null;
  kind: 'cash_in' | 'dispense' | 'manual_in' | 'manual_out' | 'arching' | 'adjust';
  amount: string;
  balance_after: string;
  denominations: Record<string, number> | null;
  reason: string | null;
  happened_at: string;
};

export type CashStatusLocal = {
  online: boolean;
  balance: number;
  denominations: Record<string, number>;
  alarms: string[];
};


export type RegisterTenantRequest = {
  business_name: string;
  admin_name: string;
  email: string;
  password: string;
  phone?: string;
  cif?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country_code?: string;
  timezone?: string;
  terms_accepted: boolean;
};

export type RegisterTenantResponse = {
  token: string;
  tenant: {
    id: string;
    slug: string;
    name: string;
    status: string;
    ticket_balance: number;
    trial_ends_at: string | null;
  };
  user: {
    id: string;
    name: string;
    email: string;
    user_type: string;
    pos_pin: string;
  };
  locale: { id: string; name: string };
};


export type FiscalSettings = {
  tenant_id: string;
  default_certificate_id: string | null;
  verifactu_mode: 'remitido' | 'no_remitido' | 'disabled';
  ticket_bai_territory: 'bizkaia' | 'gipuzkoa' | 'alava' | null;
  is_test_environment: boolean;
  software_id: string | null;
  software_name: string | null;
  software_version: string | null;
  emisor_nif: string | null;
  emisor_nombre: string | null;
  default_certificate?: {
    id: string;
    alias: string;
    subject_cn: string | null;
    valid_to: string | null;
  } | null;
};

export type FiscalCertificate = {
  id: string;
  tenant_id: string;
  alias: string;
  subject_cn: string | null;
  issuer_cn: string | null;
  serial_number: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  uploaded_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type FiscalRecord = {
  id: string;
  tenant_id: string;
  ticket_id: string;
  kind: 'verifactu' | 'ticket_bai_bizkaia' | 'ticket_bai_gipuzkoa' | 'ticket_bai_alava';
  sequence_number: number;
  hash_previous: string | null;
  hash_current: string;
  qr_url: string | null;
  qr_short_code: string | null;
  status: 'pending' | 'signed' | 'sent' | 'accepted' | 'rejected';
  signed_at: string | null;
  sent_at: string | null;
  response_status: string | null;
  retry_count: number;
  last_error: string | null;
  emitted_at: string;
  created_at: string;
  updated_at: string;
  ticket?: { id: string; fiscal_series: string | null; fiscal_number: string | null; total: string; closed_at: string | null };
};


export type DatafonoSaleResult = {
  approved: boolean;
  amount: number;
  auth_code: string | null;
  rrn: string | null;
  card_brand: string | null;
  card_last4: string | null;
  response_code: string | null;
  response_message: string | null;
  raw_response: string;
};

export type { PositecClient as Client };
export * from '@positec/types';
